import { Modal, Radio, Typography, Alert, Button } from 'antd';
import { useState } from 'react';
import { QboImportHelpModal } from './QboImportHelpModal';

type Format = 'csv' | 'qbo' | 'ofx';

// The transactions page leaves startDate empty until the customer picks a range,
// which rendered as a bare "Date range:  – 2026-08-22". An open-ended range is a
// valid request, so say so rather than showing a blank side.
export function formatExportDateRange(
  startDate: string,
  endDate: string
): string {
  if (!startDate && !endDate) return 'All time';
  if (!startDate) return `All time through ${endDate}`;
  if (!endDate) return `${startDate} onward`;
  return `${startDate} – ${endDate}`;
}

export function ExportModal({
  open,
  onClose,
  onSubmit,
  startDate,
  endDate,
  isSubmitting,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (format: Format) => void | Promise<void>;
  startDate: string;
  endDate: string;
  isSubmitting: boolean;
}) {
  const [format, setFormat] = useState<Format>('csv');
  const [helpOpen, setHelpOpen] = useState(false);

  return (
    <>
      <Modal
        open={open}
        onCancel={onClose}
        title="Export Transactions"
        footer={[
          <Button key="cancel" onClick={onClose}>
            Cancel
          </Button>,
          <Button
            key="ok"
            type="primary"
            loading={isSubmitting}
            onClick={() => onSubmit(format)}
          >
            Export
          </Button>,
        ]}
      >
        <Typography.Paragraph>
          <strong>Date range:</strong> {formatExportDateRange(startDate, endDate)}
        </Typography.Paragraph>

        <Typography.Paragraph strong style={{ marginBottom: 8 }}>
          Format
        </Typography.Paragraph>
        <Radio.Group
          value={format}
          onChange={(e) => setFormat(e.target.value)}
          style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
        >
          <Radio value="csv">TACH CSV (full transaction data)</Radio>
          <Radio value="qbo">QuickBooks (.qbo)</Radio>
          <Radio value="ofx">
            Other accounting software (.ofx) — Xero, Wave, GnuCash, etc.
          </Radio>
        </Radio.Group>

        {(format === 'qbo' || format === 'ofx') && (
          <Alert
            type="info"
            showIcon
            style={{ marginTop: 16 }}
            message="This file contains your bank account and routing numbers. Treat it like a bank statement."
          />
        )}

        {format === 'qbo' && (
          <div style={{ marginTop: 12 }}>
            <Button
              type="link"
              onClick={() => setHelpOpen(true)}
              style={{ padding: 0 }}
            >
              How to import into QuickBooks &rarr;
            </Button>
          </div>
        )}
      </Modal>

      <QboImportHelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
    </>
  );
}
